"use client";

import { Link } from "@/components/common/Link";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
import { ShieldCheck, Cpu, Lock, Zap, Award, CircleCheck as CheckCircle2, Server, Globe, FileText, ArrowRight, Eye, RefreshCw, Key } from 'lucide-react';
export default function MissionAndExpertiseStats() {
  const {
    ref: s1Ref,
    isVisible: s1Visible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.15
  });
  const {
    ref: s2Ref,
    isVisible: s2Visible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.15
  });
  const {
    ref: s3Ref,
    isVisible: s3Visible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.15
  });
  const {
    ref: s4Ref,
    isVisible: s4Visible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.15
  });
  const {
    ref: s5Ref,
    isVisible: s5Visible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.15
  });
  const {
    ref: s6Ref,
    isVisible: s6Visible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.15
  });
  const {
    ref: s7Ref,
    isVisible: s7Visible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.15
  });
  const coreValues = [{
    title: "Radical Transparency",
    description: "We tell you exactly how we test, what we found, and why we ranked each VPN the way we did. No vague scores. No mystery criteria. Every rating is backed by real data from real testing.",
    icon: ShieldCheck
  }, {
    title: "Uncompromising Independence",
    description: "VPNGENIX is 100% reader-supported. We are never paid to recommend a VPN. Our rankings are determined solely by our testing results — not by affiliate commissions or sponsorship deals.",
    icon: Lock
  }, {
    title: "Privacy For Everyone",
    description: "We believe online privacy is a fundamental human right — not a luxury for the tech-savvy. Our guides, reviews, and tools are designed to be understood by everyone, not just experts.",
    icon: Globe
  }];
  const stats = [{
    number: "15+",
    label: "Years of Combined Cybersecurity Experience"
  }, {
    number: "200+",
    label: "VPN Tests Conducted Annually"
  }, {
    number: "6",
    label: "VPNs Independently Reviewed & Ranked"
  }, {
    number: "100%",
    label: "Independent & Unsponsored Reviews"
  }];
  const protocols = [{
    title: "Independent Cryptographic Audits",
    desc: "Our zero-logs infrastructure and server code undergo rigorous annual third-party inspections to verify complete privacy integrity.",
    icon: Cpu
  }, {
    title: "RAM-Only Server Deployment",
    desc: "All network nodes operate exclusively on volatile RAM. Hard drives are physically absent, ensuring zero data persistence on reboot.",
    icon: Server
  }, {
    title: "Automated Kill Switch Protocol",
    desc: "If your encrypted tunnel drops unexpectedly for even a millisecond, our system instantly halts all traffic to prevent exposure.",
    icon: Zap
  }];
  const trustPoints = [{
    title: "Open-Source Transparency",
    desc: "Our client applications are fully open for community audit and peer review."
  }, {
    title: "Jurisdictional Privacy",
    desc: "Headquartered in privacy-friendly Switzerland, far beyond 14-Eyes surveillance reach."
  }, {
    title: "Unlimited Bandwidth",
    desc: "Zero throttling, zero data caps, and unlimited simultaneous device connections."
  }, {
    title: "DNS Leak Protection",
    desc: "Encrypted DNS queries routed securely through private tunnels on every single connection."
  }, {
    title: "Multi-Hop Routing",
    desc: "Chain your connection across multiple secure jurisdictions for maximum untraceability."
  }, {
    title: "24/7 Expert Support",
    desc: "Direct access to Tier-3 security engineers whenever you need rapid technical guidance."
  }];
  return <section data-section-id="1409" id="missionandexpertisestats" className="bg-background text-foreground relative overflow-hidden py-12 md:py-16">
      <div className="mx-auto max-w-[1360px] px-6 md:px-10 space-y-20 md:space-y-28">

        {}
        <div ref={s1Ref} data-index={0} className={["bg-card border border-border p-8 md:p-14 rounded-lg shadow-xl relative overflow-hidden text-center max-w-[900px] mx-auto", "transition-all duration-700 ease-[cubic-bezier(0.22,1,0.36,1)]", s1Visible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-4"].join(" ")}>
          <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[300px] h-[300px] rounded-lg pointer-events-none opacity-15 blur-3xl" style={{
          background: "radial-gradient(circle, var(--primary) 0%, transparent 70%)"
        }} />
          <div className="font-mono text-xs md:text-sm uppercase tracking-widest mb-4" style={{
          color: "#FF6B35",
          letterSpacing: "0.18em"
        }}>
            WHO WE ARE &amp; WHY WE EXIST
          </div>
          <h2 className="font-default text-3xl md:text-5xl font-bold text-foreground tracking-tight mb-6">
            We’re On a Mission to Make <span style={{
            color: "#FF6B35"
          }}>Online Privacy</span> Accessible to Everyone.
          </h2>
          <p className="font-default text-foreground font-semibold text-lg md:text-2xl leading-relaxed max-w-[780px] mx-auto">
            VPNGENIX was built on one simple belief: every person deserves the right to browse the internet privately, securely, and freely — regardless of where they live or what device they use.
          </p>
          <p className="font-default text-muted-foreground text-base md:text-lg leading-relaxed max-w-[780px] mx-auto mt-4">
            Our commitment is to engineer uncompromised cryptographic protection, eliminating data surveillance and empowering global citizens to reclaim complete control over their digital identities.
          </p>

          <div className="mt-12 pt-10 border-t border-border text-center">
            <div className="font-mono text-xs md:text-sm uppercase tracking-widest mb-3" style={{
            color: "#FF6B35",
            letterSpacing: "0.18em"
          }}>
              THE PEOPLE BEHIND THE REVIEWS
            </div>
            <h2 className="font-default text-2xl md:text-4xl font-bold text-foreground tracking-tight mb-6 whitespace-pre-line">
              Real Experts. Real Testing.<br />Real Results.
            </h2>
            <p className="font-default text-muted-foreground text-base md:text-lg leading-relaxed max-w-[780px] mx-auto mb-4">
              Our editorial and testing team consists of veteran cybersecurity researchers, ethical hackers, and privacy advocates with decades of combined experience in network security and cryptography.
            </p>
            <p className="font-default text-muted-foreground text-base md:text-lg leading-relaxed max-w-[780px] mx-auto">
              We do not rely on marketing brochures or sponsored claims. Every VPN recommendation is forged through rigorous, hands-on lab stress tests, live traffic analysis, and deep-packet inspections to ensure your absolute protection online.
            </p>
          </div>
        </div>

        {}
        <div ref={s2Ref} data-index={1} className={["transition-all duration-700 ease-[cubic-bezier(0.22,1,0.36,1)]", s2Visible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-4"].join(" ")}>
          <div className="text-center max-w-[700px] mx-auto mb-12">
            <div className="font-mono text-xs md:text-sm uppercase text-muted-foreground mb-3" style={{
            letterSpacing: "0.18em"
          }}>
              § 02 — Core Values
            </div>
            <h2 className="font-default text-2xl md:text-4xl font-bold tracking-tight">
              Built on Unshakable Principles
            </h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {coreValues.map((val, idx) => {
            const IconComp = val.icon;
            return <div key={idx} data-index={idx} className="bg-card border-t-4 border-t-primary border border-border p-8 rounded-lg shadow-md hover:shadow-xl transition-all duration-300 flex flex-col justify-between">
                  <div>
                    <div className="w-12 h-12 rounded-lg bg-primary/10 flex items-center justify-center text-primary mb-6">
                      <IconComp className="w-6 h-6" />
                    </div>
                    <h3 className="font-default text-xl font-semibold mb-3 text-foreground">{val.title}</h3>
                    <p className="font-default text-muted-foreground text-sm leading-relaxed">{val.description}</p>
                  </div>
                  <div className="mt-8 pt-4 border-t border-border flex items-center text-xs font-mono text-primary uppercase">
                    <span>Verified Standard</span>
                  </div>
                </div>;
          })}
          </div>
        </div>

        {}
        <div ref={s3Ref} data-index={2} className={["bg-card border border-border p-8 md:p-12 rounded-lg shadow-xl", "transition-all duration-700 ease-[cubic-bezier(0.22,1,0.36,1)]", s3Visible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-4"].join(" ")}>
          <div className="text-center max-w-[700px] mx-auto mb-12">
            <div className="font-mono text-xs md:text-sm uppercase text-muted-foreground mb-3" style={{
            letterSpacing: "0.18em"
          }}>
              § 03 — Expertise &amp; Scale
            </div>
            <h2 className="font-default text-2xl md:text-4xl font-bold tracking-tight">
              Engineered by Security Specialists
            </h2>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-8">
            {stats.map((st, idx) => <div key={idx} data-index={idx} className="bg-background border border-border p-6 rounded-lg text-center shadow-sm flex flex-col items-center justify-center">
                <div className="font-default font-extrabold mb-2" style={{
              fontSize: "clamp(2.5rem, 4vw, 3.5rem)",
              lineHeight: 1.1,
              color: "#FF6B35"
            }}>
                  {st.number}
                </div>
                <div className="font-default font-semibold text-foreground text-base">{st.label}</div>
              </div>)}
          </div>
        </div>

        {}
        <div ref={s4Ref} data-index={3} className={["transition-all duration-700 ease-[cubic-bezier(0.22,1,0.36,1)]", s4Visible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-4"].join(" ")}>
          <div className="text-center max-w-[700px] mx-auto mb-12">
            <div className="font-mono text-xs md:text-sm uppercase text-muted-foreground mb-3" style={{
            letterSpacing: "0.18em"
          }}>
              § 04 — Testing Protocol
            </div>
            <h2 className="font-default text-2xl md:text-4xl font-bold tracking-tight">
              Rigorous Security Verification
            </h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {protocols.map((proto, idx) => {
            const ProtoIcon = proto.icon;
            return <div key={idx} data-index={idx} className={`border border-border p-8 rounded-lg shadow-sm flex flex-col sm:flex-row gap-6 items-start ${idx % 2 === 0 ? "bg-card" : "bg-background"}`}>
                  <div className="w-12 h-12 rounded-lg bg-primary/10 flex-shrink-0 flex items-center justify-center text-primary">
                    <ProtoIcon className="w-6 h-6" />
                  </div>
                  <div>
                    <h3 className="font-default text-xl font-semibold mb-2 text-foreground">{proto.title}</h3>
                    <p className="font-default text-muted-foreground text-sm leading-relaxed">{proto.desc}</p>
                  </div>
                </div>;
          })}
          </div>
        </div>

        {}
        <div ref={s5Ref} data-index={4} className={["bg-card border border-border border-l-4 border-l-primary p-8 md:p-12 rounded-lg shadow-xl max-w-[700px] mx-auto", "transition-all duration-700 ease-[cubic-bezier(0.22,1,0.36,1)]", s5Visible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-4"].join(" ")}>
          <div className="font-mono text-xs md:text-sm uppercase text-muted-foreground mb-3" style={{
          letterSpacing: "0.18em"
        }}>
            § 05 — Editorial Independence
          </div>
          <h3 className="font-default text-2xl md:text-3xl font-bold text-foreground mb-4">
            Zero Commercial Compromise
          </h3>
          <p className="font-default text-muted-foreground text-base leading-relaxed">
            Our research, reviews, and security analysis are conducted with absolute editorial independence. We do not accept sponsored rankings or paid endorsements. Every conclusion is backed by empirical data, packet analysis, and real-world stress testing.
          </p>
        </div>

        {}
        <div ref={s6Ref} data-index={5} className={["transition-all duration-700 ease-[cubic-bezier(0.22,1,0.36,1)]", s6Visible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-4"].join(" ")}>
          <div className="text-center max-w-[700px] mx-auto mb-12">
            <div className="font-mono text-xs md:text-sm uppercase text-muted-foreground mb-3" style={{
            letterSpacing: "0.18em"
          }}>
              § 06 — Trust Points
            </div>
            <h2 className="font-default text-2xl md:text-4xl font-bold tracking-tight">
              Why Millions Trust VPNGENIX
            </h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {trustPoints.map((tp, idx) => <div key={idx} data-index={idx} className="bg-card border border-border p-6 rounded-lg shadow-sm hover:border-primary transition-all duration-300">
                <div className="flex items-center gap-3 mb-3">
                  <CheckCircle2 className="w-5 h-5 text-primary flex-shrink-0" />
                  <h3 className="font-default text-lg font-semibold text-foreground">{tp.title}</h3>
                </div>
                <p className="font-default text-muted-foreground text-sm leading-relaxed">{tp.desc}</p>
              </div>)}
          </div>
        </div>

        {}
        <div ref={s7Ref} data-index={6} className={["bg-card border border-border p-10 md:p-16 rounded-lg shadow-2xl relative overflow-hidden text-center", "transition-all duration-700 ease-[cubic-bezier(0.22,1,0.36,1)]", s7Visible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-4"].join(" ")}>
          <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[400px] h-[400px] rounded-lg pointer-events-none opacity-20 blur-3xl" style={{
          background: "radial-gradient(circle, var(--primary) 0%, transparent 70%)"
        }} />
          <div className="font-mono text-xs md:text-sm uppercase text-muted-foreground mb-4" style={{
          letterSpacing: "0.18em"
        }}>
            § 07 — Command Center
          </div>
          <h2 className="font-default text-3xl md:text-5xl font-bold text-foreground tracking-tight mb-6 max-w-[800px] mx-auto">
            Ready to Secure Your Digital Sovereignty?
          </h2>
          <p className="font-default text-muted-foreground text-base md:text-lg max-w-[650px] mx-auto mb-10 leading-relaxed">
            Join the decentralized security network engineered to protect your privacy without compromise. Activate your protection in under 60 seconds.
          </p>

          <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
            <Link to="/VpnGenixPage" className="w-full sm:w-auto inline-flex items-center justify-center px-8 py-4 rounded-lg bg-primary text-primary-foreground font-semibold shadow-lg hover:translate-y-[-2px] transition-all duration-300">
              Get Protected Now
              <ArrowRight className="w-4 h-4 ml-2" />
            </Link>
            <Link to="#" className="w-full sm:w-auto inline-flex items-center justify-center px-8 py-4 rounded-lg border border-primary text-foreground bg-transparent font-semibold hover:bg-primary/10 transition-all duration-300">
              Compare Protocols
            </Link>
          </div>
        </div>

      </div>
    </section>;
}